import { Server } from './server';
import { Service, ServiceResponse } from './service';

const PROFILE_CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

export class PublicService extends Service {
  protected profiles:any;
  protected communities:any;
  protected unfurlCache:string[];

  constructor() {
    super();
    this.profiles = [];
    this.communities = [];
    this.unfurlCache = [];
  }

  public async init(): Promise<ServiceResponse> {
    await Server.database.purge('USER#', PROFILE_CACHE_EXPIRATION);
    this.profiles = await Server.database.query('USER#');
    return {success: true};
  }

  public getProfile(id:string) {
    let profile = this.profiles.find(profile => profile.id == id);
    return this.fixProfile(profile);
  }

  public getProfileFromSlug(slug:string) {
    let profile = this.profiles.find(profile => profile.slug == slug);
    return this.fixProfile(profile);
  }

  protected fixProfile(profile:any) {
    if(profile) {
      if (profile.portrait && profile.portrait.indexOf('https://s3.us-west-2.amazonaws.com/oneclick.media') !== -1) 
        profile.portrait = profile.portrait.replace('https://s3.us-west-2.amazonaws.com/oneclick.media', 'https://d27jhqexqrz634.cloudfront.net');
  
      if (profile.banner && profile.banner.indexOf('https://s3.us-west-2.amazonaws.com/oneclick.media') !== -1) 
        profile.banner = profile.banner.replace('https://s3.us-west-2.amazonaws.com/oneclick.media', 'https://d27jhqexqrz634.cloudfront.net');
    }

    return profile;
  }

  public async loadProfiles(ids:string[]) {
    let missing = ids.filter(id => !this.profiles.find(profile => profile.id === id));
    if(missing.length == 0)
      return {success: true};

    let response = await this.sendCommand('public', 'get-profiles', {ids: missing});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    let profiles = response.body.profiles;
    for(let i = 0; i < profiles.length; i++) 
      this.addProfileToCache(profiles[i]);

    return {success: true};
  }

  public async loadProfileFromSlug(slug:string) {
    let profile = this.profiles.find(profile => profile.slug == slug);
    profile = this.fixProfile(profile);

    if(profile)
      return {success: true, profile};

    let response = await this.sendCommand('public', 'get-profile', {slug});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    profile = response.body.profile;
    this.addProfileToCache(profile);

    return {success: true, profile};
  }

  public async addProfileToCache(profile:any) {
    let existing = this.getProfile(profile.id);
    if(existing) {
      let oldProfile = JSON.stringify(existing);
      let newProfile = JSON.stringify(profile);
      if(oldProfile == newProfile)
        return;
    }

    if(!profile.friends) profile.friends = [];
    if(!profile.following) profile.following = [];
    if(!profile.followers) profile.followers = [];

    profile = this.fixProfile(profile);

    if(existing) {
      let index = this.profiles.findIndex(p => p.id === profile.id);
      this.profiles[index] = profile;
    } else 
      this.profiles.push(profile);

    await Server.database.putItem(`USER#${profile.id}`, profile);
  }

  public async removeProfileFromCache(profileId: string) {
    let index = this.profiles.findIndex(p => p.id === profileId);
    if (index !== -1) 
      this.profiles.splice(index, 1);
    await Server.database.removeItem(`USER#${profileId}`);
  }

  public async unfurl(url:string) {
    if(this.unfurlCache[url])
      return {success: true, data: this.unfurlCache[url]};

    let response = await this.sendCommand('public', 'unfurl', {url});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.unfurlCache[url] = response.body;

    return {success: true, data: response.body};
  }

  public unfurlFromCache(url:string) {
    return this.unfurlCache[url];
  }

  public async getTwitterEmbed(url:string) {
    let response = await this.sendCommand('public', 'get-twitter-embed', {url});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, embed: response.body.embed};
  }

  // communities
  public getCommunity(id:string) {
    return this.communities[id];
  }
  
  public addCommunityToCache(community:any) {
    this.communities[community.id] = community;
    this.communities[community.slug] = community;
  }

  public async loadCommunities(ids:string[]) {
    let missing = [];
    for(let i = 0; i < ids.length; i++)
      if(!this.communities[ids[i]])
        missing.push(ids[i]);

    if(missing.length == 0)
      return {success: true};

    let response = await this.sendCommand('public', 'get-communities', {ids: missing});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    let communities = response.body.communities;

    for(let i = 0; i < communities.length; i++) 
      this.addCommunityToCache(communities[i]);

    return {success: true};
  }

  public async loadCommunity(slug:string) {
    let response = await this.sendCommand('public', 'get-community', {slug});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.addCommunityToCache(response.body.community);

    return {success: true, community: response.body.community};
  }

  public async findCommunities(params:any) {
    let response = await this.sendCommand('public', 'find-communities', params);

    if(response.status != 200)
      return {success: false, message: response.body.message};

    let communities = response.body.communities;

    for(let i = 0; i < communities.length; i++) 
      this.addCommunityToCache(communities[i]);

    return {success: true, communities};
  }

  public async getCommunityMembers() {
    let response = await this.sendCommand('public', 'get-community-members');
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, members: response.body.members};
  }

  public async getCommunityInviteCount(email:string) {
    let response = await this.sendCommand('public', 'get-community-invite-count', {email});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, count: response.body.count};
  }

  public async getCommunityInvite(id:string) {
    let response = await this.sendCommand('public', 'get-community-invite', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, invite: response.body.invite};
  }

  public async declineCommunityInvite(params:any) {
    let response = await this.sendCommand('public', 'decline-community-invite', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, invite: response.body.invite};
  }

  public async getStatus() {
    let response = await this.sendCommand('public', 'get-status');
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, status: response.body.status};
  }
}
